/**
 * Mini-card d'un employé avec résumé de ses soldes de congés.
 *
 * Usage : tableau de bord secrétariat pour voir d'un coup d'œil qui est
 * proche de dépasser son quota. Clic pour voir le détail complet.
 */
import { type LeaveBalance } from '../utils/leaveBalanceUtils';
import { AlertCircle, Calendar, User } from 'lucide-react';

export interface EmployeeBalanceCardProps {
  employeeId: string;
  employeeName: string;
  balances: LeaveBalance[];
  onClick?: () => void;
  /** Si true, met en avant les employés avec 1+ congé en attente */
  highlightPending?: boolean;
}

export function EmployeeBalanceCard({
  employeeName, balances, onClick, highlightPending = true,
}: EmployeeBalanceCardProps) {
  // Mise en avant du quota principal (CP en priorité)
  const mainBalance = balances.find(b => b.type === 'cp')
    ?? balances.find(b => b.totalDays !== undefined)
    ?? balances[0];

  const hasQuota = mainBalance?.totalDays !== undefined;
  const pct = hasQuota && mainBalance.totalDays! > 0
    ? Math.min(100, (mainBalance.consumed / mainBalance.totalDays!) * 100)
    : 0;

  const barColor =
    !hasQuota ? '#cbd5e1'
    : pct > 90 ? '#dc2626'
    : pct > 70 ? '#f59e0b'
    : '#10b981';

  const totalPending = balances.reduce((sum, b) => sum + b.pending, 0);
  const hasPending = totalPending > 0;

  const initials = employeeName
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left',
        background: 'white',
        border: hasPending && highlightPending ? '1px solid #fde68a' : '1px solid #e2e8f0',
        borderRadius: '14px',
        padding: '16px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
        fontFamily: 'inherit',
      }}
      onMouseEnter={e => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,23,42,0.08)';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)';
      }}
    >
      {/* Header avec avatar + nom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
          color: '#6d28d9', fontWeight: 700, fontSize: '0.85rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {initials || <User size={18} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700, color: '#0f172a', fontSize: '0.95rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {employeeName}
          </div>
          {hasPending && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '0.72rem', color: '#b45309', fontWeight: 600, marginTop: '2px',
            }}>
              <AlertCircle size={11} /> {totalPending}j en attente de validation
            </div>
          )}
        </div>
      </div>

      {/* Solde principal avec barre */}
      {mainBalance && (
        <div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            marginBottom: '6px',
          }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {labelFor(mainBalance.type)}
            </span>
            <span style={{ fontSize: '0.85rem', color: '#1e293b' }}>
              <strong style={{ color: barColor, fontSize: '1rem' }}>{mainBalance.consumed}</strong>
              {hasQuota && (
                <>
                  <span style={{ margin: '0 3px', color: '#94a3b8' }}>/</span>
                  <span>{mainBalance.totalDays}</span>
                </>
              )}
              <span style={{ color: '#94a3b8', marginLeft: '4px', fontSize: '0.75rem' }}>j</span>
            </span>
          </div>
          <div style={{
            height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: `${pct}%`, background: barColor, transition: 'width 0.3s',
            }} />
            {/* Traceur pending : zone hachée supplémentaire */}
            {hasQuota && mainBalance.pending > 0 && (
              <div style={{
                position: 'absolute', top: 0, bottom: 0,
                left: `${pct}%`,
                width: `${Math.min(100 - pct, (mainBalance.pending / mainBalance.totalDays!) * 100)}%`,
                background: 'repeating-linear-gradient(45deg, #fde68a, #fde68a 4px, #fef3c7 4px, #fef3c7 8px)',
              }} />
            )}
          </div>
          {!hasQuota && (
            <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
              Pas de quota défini
            </div>
          )}
        </div>
      )}

      {/* Autres types compact */}
      {balances.length > 1 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '6px',
          paddingTop: '10px', borderTop: '1px dashed #e2e8f0',
        }}>
          {balances.filter(b => b !== mainBalance && (b.consumed > 0 || b.pending > 0)).map(b => (
            <span key={b.type} style={{
              fontSize: '0.72rem', padding: '3px 8px',
              background: '#f8fafc', color: '#475569',
              borderRadius: '6px', fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', gap: '4px',
            }}>
              <Calendar size={10} />
              {labelShort(b.type)}: <strong>{b.consumed}</strong>
              {b.pending > 0 && <span style={{ color: '#d97706' }}>+{b.pending}</span>}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

const LABELS_FULL: Record<string, string> = {
  cp: 'Congés payés',
  formation: 'Formation',
  autre: 'Autre',
  maladie: 'Maladie',
  sans_solde: 'Sans solde',
};

const LABELS_SHORT: Record<string, string> = {
  cp: 'CP',
  formation: 'Formation',
  autre: 'Autre',
  maladie: 'Maladie',
  sans_solde: 'Sss',
};

function labelFor(type: string): string { return LABELS_FULL[type] ?? type; }
function labelShort(type: string): string { return LABELS_SHORT[type] ?? type; }
